import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  fetchSealedProduct,
  searchSealedProducts,
  type SealedContentLine,
  type SealedLookupItem,
  type SealedLookupResponse,
  type SealedMissingLine,
  type SealedSearchHit,
} from '../api/sealedLookup';
import { InventorySectionNav } from '../components/inventory/InventorySectionNav';
import { WalkThumb } from './OrderPullPage';
import {
  feePercent,
  finishLabel,
  formatCrackMoney,
  formatLineMoney,
  formatReportMoney,
  formatSealedMoney,
  formatSealedReleaseDate,
  sealedProductTypeLabel,
} from '../lib/sealedValuationFormat';
import {
  borderRadius,
  colorExtensions,
  colors,
  spacing,
  typography,
  typographyFlat,
} from '../styles/tokens';

const SOURCE_LABELS: Record<string, string> = {
  card: 'Bonus',
  'deck:mainBoard': 'Main',
  'deck:commander': 'Commander',
  'deck:sideBoard': 'Sideboard',
  'deck:displayCommander': 'Display',
  'deck:tokens': 'Token',
};

function errorMessage(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export const SealedLookupPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get('q') || '');
  const [hits, setHits] = useState<SealedSearchHit[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [listOpen, setListOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [pricing, setPricing] = useState(false);
  const [pricingName, setPricingName] = useState<string | null>(null);
  const [detail, setDetail] = useState<SealedLookupResponse | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const searchRequest = useRef(0);
  const priceRequest = useRef(0);
  const pendingName = useRef<string | null>(null);
  const productParam = params.get('product') || '';

  const loadProduct = useCallback(async (hit: { sealed_uuid: string; name?: string | null }) => {
    const current = ++priceRequest.current;
    setPricing(true);
    setPricingName(hit.name || 'this sealed product');
    setDetail(null);
    setDetailError(null);
    setListOpen(false);
    try {
      const data = await fetchSealedProduct(hit.sealed_uuid);
      if (current !== priceRequest.current) return;
      setDetail(data);
      setPricingName(data.item.name);
    } catch (err: unknown) {
      if (current !== priceRequest.current) return;
      setDetailError(errorMessage(err, 'Failed to price this sealed product'));
    } finally {
      if (current === priceRequest.current) setPricing(false);
    }
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setHits([]);
      setTruncated(false);
      setSearching(false);
      setSearchError(null);
      return;
    }
    setSearching(true);
    setSearchError(null);
    const current = ++searchRequest.current;
    const handle = window.setTimeout(() => {
      void searchSealedProducts(q)
        .then((data) => {
          if (current !== searchRequest.current) return;
          setHits(data.items);
          setTruncated(data.truncated);
          setActiveIndex(0);
          setListOpen(true);
        })
        .catch((err: unknown) => {
          if (current !== searchRequest.current) return;
          setHits([]);
          setTruncated(false);
          setSearchError(errorMessage(err, 'Search failed'));
        })
        .finally(() => {
          if (current === searchRequest.current) setSearching(false);
        });
    }, 250);
    return () => window.clearTimeout(handle);
  }, [query]);

  useEffect(() => {
    if (!productParam) return;
    void loadProduct({
      sealed_uuid: productParam,
      name: pendingName.current,
    });
  }, [productParam, loadProduct]);

  const choose = (hit: SealedSearchHit) => {
    pendingName.current = hit.name;
    const next = new URLSearchParams(params);
    const q = query.trim();
    if (q) next.set('q', q);
    else next.delete('q');
    next.set('product', hit.sealed_uuid);
    setParams(next, { replace: true });
  };

  const onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!listOpen || hits.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, hits.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const hit = hits[activeIndex];
      if (hit) choose(hit);
    } else if (event.key === 'Escape') {
      setListOpen(false);
    }
  };

  const trimmed = query.trim();
  const item = detail?.item;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Sealed Lookup</h1>
        <p style={styles.subtitle}>
          Search one Secret Lair drop, Secret Lair bundle, or a Commander, Challenger, Event, or
          Brawl deck. Sealed value is the unopened quote. Opened value is the contents if you crack
          it and sell the cards.
        </p>
        <InventorySectionNav />
      </div>

      <div style={styles.searchWrap}>
        <label style={styles.searchLabel} htmlFor="sealed-lookup-search">
          Sealed product
        </label>
        <input
          id="sealed-lookup-search"
          role="combobox"
          aria-expanded={listOpen && hits.length > 0}
          aria-controls="sealed-lookup-results"
          aria-autocomplete="list"
          value={query}
          placeholder="Name or set, for example Food and Fellowship or SLD"
          onChange={(event) => {
            setQuery(event.target.value);
            setListOpen(true);
          }}
          onFocus={() => {
            if (hits.length > 0) setListOpen(true);
          }}
          onKeyDown={onSearchKeyDown}
          style={styles.searchInput}
        />
        {trimmed.length === 1 && (
          <p style={styles.hint}>Type at least 2 characters.</p>
        )}
        {searching && (
          <div role="status" aria-live="polite" style={styles.statusLine}>
            Searching sealed products for “{trimmed}”…
          </div>
        )}
        {searchError && <div style={styles.error}>{searchError}</div>}
        {listOpen && !searching && trimmed.length >= 2 && hits.length === 0 && !searchError && (
          <p style={styles.hint}>No Secret Lair or precon matches.</p>
        )}
        {listOpen && hits.length > 0 && (
          <ul id="sealed-lookup-results" role="listbox" style={styles.results}>
            {hits.map((hit, index) => (
              <li key={hit.sealed_uuid} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={index === activeIndex}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => choose(hit)}
                  style={{
                    ...styles.resultButton,
                    ...(index === activeIndex ? styles.resultButtonActive : {}),
                  }}
                >
                  <span style={styles.resultName}>{hit.name}</span>
                  <span style={styles.resultMeta}>
                    {hit.set}
                    {hit.set_name ? ` · ${hit.set_name}` : ''}
                    {' · '}
                    {sealedProductTypeLabel(hit.product_type)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {truncated && listOpen && hits.length > 0 && (
          <p style={styles.hint}>Showing the first matches. Type more of the name to narrow it.</p>
        )}
      </div>

      {!pricing && !item && !detailError && !productParam && (
        <div style={styles.empty}>
          <div style={styles.emptyTitle}>Search for one sealed product</div>
          <div style={styles.emptyBody}>
            Prices load after you pick a result. The page does not value the whole catalog.
          </div>
        </div>
      )}

      {pricing && (
        <div role="status" aria-live="polite" aria-busy="true" style={styles.loadingPanel}>
          <div style={styles.loadingTitle}>Pricing {pricingName}…</div>
          <p style={styles.loadingBody}>
            Loading sealed value, opened value, and the card list for this product.
          </p>
        </div>
      )}

      {detailError && <div style={styles.error}>{detailError}</div>}

      {item && detail && !pricing && (
        <ProductDetail item={item} feeRate={detail.tcg_fee_rate} />
      )}
    </div>
  );
};

function ProductDetail({ item, feeRate }: { item: SealedLookupItem; feeRate: number }) {
  const openedGross = formatCrackMoney(item.complete, item.crack_gross);
  const openedNet = formatCrackMoney(item.complete, item.crack_net);
  return (
    <section style={styles.detail} aria-label={item.name}>
      <div style={styles.detailHeader}>
        <div>
          <h2 style={styles.productName}>{item.name}</h2>
          <p style={styles.productMeta}>
            {sealedProductTypeLabel(item.product_type)}
            {item.set ? ` · ${item.set}` : ''}
            {item.set_name ? ` · ${item.set_name}` : ''}
            {item.release_date ? ` · ${formatSealedReleaseDate(item.release_date)}` : ''}
          </p>
        </div>
        {item.recommendation ? <RecommendationBadge recommendation={item.recommendation} /> : null}
      </div>

      <div style={styles.summaryGrid}>
        <SummaryCard label="Sealed gross" value={formatSealedMoney(item.sealed_gross)} />
        <SummaryCard label="Sealed net" value={formatSealedMoney(item.sealed_net)} />
        <SummaryCard label="Opened gross" value={openedGross ?? 'Incomplete'} />
        <SummaryCard label="Opened net" value={openedNet ?? 'Incomplete'} />
      </div>

      <p style={styles.note}>
        Soft-high picks the best list across TCG and Manapool. When TCG wins, sealed net applies
        the {feePercent(feeRate)} TCGPlayer fee. A Manapool win is already a sell price, so its
        gross and net match. Hold means the best net does not clear the highest on-hand unit
        cost. Etched cards are priced as foil.
      </p>

      <div style={styles.facts}>
        <span>
          On hand: <strong>{item.on_hand_qty}</strong>
        </span>
        {item.on_hand_qty > 0 && (
          <span>
            Cost: <strong>{formatReportMoney(item.acquisition_cost_sum)}</strong>
          </span>
        )}
        {item.on_hand_qty > 0 && item.unit_cost != null && (
          <span>
            Highest unit cost: <strong>{formatReportMoney(item.unit_cost)}</strong>
          </span>
        )}
        <Completeness item={item} />
      </div>

      <MissingList lines={item.missing} />
      <ExtrasNote item={item} />

      {item.contents.length > 0 && (
        <ContentsTable
          title={item.product_type === 'secret_lair_bundle' ? 'Bonus cards' : 'Contents'}
          rows={item.contents}
        />
      )}

      {item.children.length > 0 && (
        <div style={styles.children}>
          <h3 style={styles.sectionTitle}>Inside the bundle</h3>
          {item.children.map((child) => (
            <ChildBlock key={`${child.sealed_uuid}-${child.qty}`} child={child} />
          ))}
        </div>
      )}
    </section>
  );
}

function ChildBlock({ child }: { child: SealedLookupItem }) {
  const opened = formatCrackMoney(child.complete, child.crack_net);
  return (
    <article style={styles.child}>
      <div style={styles.childHeader}>
        <div>
          <div style={styles.childName}>
            {child.qty}× {child.name}
          </div>
          <div style={styles.resultMeta}>
            {sealedProductTypeLabel(child.product_type)}
            {child.set ? ` · ${child.set}` : ''}
            {child.counted_as === 'sealed' ? ' · Counted at sealed value' : ''}
            {child.counted_as === 'crack' ? ' · Counted at opened value' : ''}
          </div>
        </div>
        <div style={styles.childPrices}>
          <span>Sealed net {formatSealedMoney(child.sealed_net)}</span>
          <span>Opened net {opened ?? 'Incomplete'}</span>
          {child.recommendation ? <RecommendationBadge recommendation={child.recommendation} /> : null}
        </div>
      </div>
      <MissingList lines={child.missing} />
      {child.contents.length > 0 && <ContentsTable title="Contents" rows={child.contents} />}
      {child.children.map((nested) => (
        <ChildBlock key={`${nested.sealed_uuid}-${nested.qty}`} child={nested} />
      ))}
    </article>
  );
}

function ContentsTable({ title, rows }: { title: string; rows: SealedContentLine[] }) {
  return (
    <div style={styles.tableBlock}>
      <h3 style={styles.sectionTitle}>{title}</h3>
      <p style={styles.hint}>Sorted by line value. Unit and line are NM net after the TCG fee.</p>
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Card</th>
              <th style={styles.th}>Finish</th>
              <th style={styles.thNum}>Qty</th>
              <th style={styles.thNum}>Unit</th>
              <th style={styles.thNum}>Line</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.card_uuid}-${row.finish}-${row.source}`}>
                <td style={styles.td}>
                  <div style={styles.cardCell}>
                    <WalkThumb imageUrl={row.image_url} />
                    <span>
                      <span style={styles.cardName}>{row.name}</span>
                      <span style={styles.cardMeta}>
                        {[
                          row.collector_number,
                          SOURCE_LABELS[row.source],
                          row.optional ? 'Optional' : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </span>
                  </div>
                </td>
                <td style={styles.td}>{finishLabel(row.finish)}</td>
                <td style={styles.tdNum}>{row.qty}</td>
                <td style={styles.tdNum}>{formatLineMoney(row.unit_net)}</td>
                <td style={styles.tdNum}>{formatLineMoney(row.line_net)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Completeness({ item }: { item: SealedLookupItem }) {
  if (item.complete) return <span style={styles.completeBadge}>Complete</span>;
  return (
    <span style={styles.completeness}>
      <span style={styles.incompleteBadge}>Incomplete</span>
      <span style={styles.missingCount}>{item.missing_count} missing</span>
    </span>
  );
}

function MissingList({ lines }: { lines: SealedMissingLine[] }) {
  if (lines.length === 0) return null;
  return (
    <ul style={styles.missingList}>
      {lines.map((line, index) => (
        <li key={`${line.kind}-${line.label}-${index}`}>
          {line.qty > 1 ? `${line.qty}× ` : ''}
          {line.label}
          {line.finish ? ` (${finishLabel(line.finish)})` : ''}
          {line.kind === 'unpriced_card' ? ' — no NM quote' : ''}
        </li>
      ))}
    </ul>
  );
}

function ExtrasNote({ item }: { item: SealedLookupItem }) {
  if (item.extras.length === 0) return null;
  return (
    <p style={styles.hint}>
      {item.extras.map((extra) => `${extra.qty}× ${extra.label}`).join(' · ')}
      . This does not change the recommendation.
    </p>
  );
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div style={styles.card}>
      <div style={styles.cardLabel}>{label}</div>
      <div style={styles.cardValue}>{value}</div>
    </div>
  );
}

function RecommendationBadge({ recommendation }: { recommendation: string }) {
  const tone =
    recommendation === 'Sell sealed'
      ? styles.recommendSell
      : recommendation === 'Crack'
        ? styles.recommendCrack
        : styles.recommendHold;
  return <span style={{ ...styles.recommendBadge, ...tone }}>{recommendation}</span>;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.md,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  title: {
    margin: 0,
    fontSize: typography.h1.fontSize,
    fontWeight: typography.h1.fontWeight,
    color: colors.text,
  },
  subtitle: {
    margin: 0,
    maxWidth: '760px',
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
  searchWrap: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
    maxWidth: '720px',
  },
  searchLabel: {
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
  },
  searchInput: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: typography.body.fontSize,
    color: colors.text,
    backgroundColor: colors.background,
    width: '100%',
  },
  hint: {
    margin: 0,
    color: colors.textSecondary,
    fontSize: typography.caption.fontSize,
  },
  statusLine: {
    margin: 0,
    padding: `${spacing.sm} ${spacing.md}`,
    borderRadius: borderRadius.sm,
    backgroundColor: '#e3f2fd',
    color: colorExtensions.infoDark,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  results: {
    listStyle: 'none',
    margin: 0,
    padding: spacing.xs,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
    maxHeight: '320px',
    overflowY: 'auto',
  },
  resultButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '2px',
    width: '100%',
    textAlign: 'left',
    border: 'none',
    background: 'transparent',
    borderRadius: borderRadius.sm,
    padding: `${spacing.xs} ${spacing.sm}`,
    cursor: 'pointer',
  },
  resultButtonActive: {
    backgroundColor: colors.gray100,
  },
  resultName: {
    color: colors.text,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  resultMeta: {
    color: colors.textSecondary,
    fontSize: typography.caption.fontSize,
  },
  empty: {
    padding: spacing.lg,
    border: `1px dashed ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.background,
  },
  emptyTitle: {
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
  },
  emptyBody: {
    marginTop: spacing.xs,
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
  },
  loadingPanel: {
    padding: spacing.lg,
    border: `1px solid ${colors.primary}`,
    borderRadius: borderRadius.md,
    backgroundColor: '#e3f2fd',
  },
  loadingTitle: {
    fontSize: typography.h4.fontSize,
    fontWeight: typography.h4.fontWeight,
    color: colorExtensions.infoDark,
  },
  loadingBody: {
    margin: `${spacing.xs} 0 0`,
    fontSize: typography.small.fontSize,
    color: colors.text,
  },
  error: {
    padding: spacing.md,
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.error}`,
    backgroundColor: colors.errorLight,
    color: colorExtensions.errorDark,
    fontSize: typography.body.fontSize,
  },
  detail: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.md,
  },
  detailHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: spacing.md,
    flexWrap: 'wrap',
  },
  productName: {
    margin: 0,
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
    color: colors.text,
  },
  productMeta: {
    margin: `${spacing.xs} 0 0`,
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: spacing.sm,
  },
  card: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
    padding: spacing.sm,
  },
  cardLabel: {
    color: colors.textSecondary,
    fontSize: typography.caption.fontSize,
  },
  cardValue: {
    marginTop: spacing.xs,
    color: colors.text,
    fontSize: typography.h4.fontSize,
    fontWeight: typography.h4.fontWeight,
  },
  note: {
    margin: 0,
    maxWidth: '760px',
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
  },
  facts: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.md,
    alignItems: 'center',
    fontSize: typography.small.fontSize,
    color: colors.text,
  },
  completeness: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: spacing.xs,
  },
  missingCount: {
    color: colors.textSecondary,
    fontSize: typography.caption.fontSize,
  },
  missingList: {
    margin: 0,
    paddingLeft: spacing.lg,
    color: colors.text,
    fontSize: typography.small.fontSize,
  },
  incompleteBadge: {
    display: 'inline-block',
    backgroundColor: '#fff3e0',
    color: colorExtensions.warningDark,
    borderRadius: borderRadius.full,
    padding: `2px ${spacing.sm}`,
    fontSize: typography.caption.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  completeBadge: {
    display: 'inline-block',
    backgroundColor: '#e8f5e9',
    color: colorExtensions.successDark,
    borderRadius: borderRadius.full,
    padding: `2px ${spacing.sm}`,
    fontSize: typography.caption.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  recommendBadge: {
    display: 'inline-block',
    borderRadius: borderRadius.full,
    padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    whiteSpace: 'nowrap',
  },
  recommendSell: {
    backgroundColor: '#e3f2fd',
    color: colorExtensions.infoDark,
  },
  recommendCrack: {
    backgroundColor: '#fff3e0',
    color: colorExtensions.warningDark,
  },
  recommendHold: {
    backgroundColor: colors.gray200,
    color: colors.gray800,
  },
  tableBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  sectionTitle: {
    margin: 0,
    fontSize: typography.h4.fontSize,
    fontWeight: typography.h4.fontWeight,
    color: colors.text,
  },
  tableWrap: {
    overflowX: 'auto',
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typography.caption.fontSize,
    color: colors.textSecondary,
    fontWeight: typographyFlat.fontWeight.semibold,
    borderBottom: `1px solid ${colors.border}`,
    backgroundColor: colors.gray100,
  },
  thNum: {
    textAlign: 'right',
    padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typography.caption.fontSize,
    color: colors.textSecondary,
    fontWeight: typographyFlat.fontWeight.semibold,
    borderBottom: `1px solid ${colors.border}`,
    backgroundColor: colors.gray100,
    whiteSpace: 'nowrap',
  },
  td: {
    padding: `2px ${spacing.sm}`,
    fontSize: typography.caption.fontSize,
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
    verticalAlign: 'middle',
  },
  tdNum: {
    padding: `2px ${spacing.sm}`,
    fontSize: typography.caption.fontSize,
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
    textAlign: 'right',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  },
  cardCell: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    minHeight: '36px',
  },
  cardName: {
    display: 'block',
    fontSize: typography.caption.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    lineHeight: '1.2',
  },
  cardMeta: {
    display: 'block',
    color: colors.textSecondary,
    fontSize: typography.caption.fontSize,
    lineHeight: '1.2',
  },
  children: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
  },
  child: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: spacing.sm,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
    backgroundColor: colors.surface,
  },
  childHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: spacing.md,
    flexWrap: 'wrap',
  },
  childName: {
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
  },
  childPrices: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.sm,
    alignItems: 'center',
    fontSize: typography.caption.fontSize,
    color: colors.text,
  },
};

export default SealedLookupPage;
